import { motion } from "framer-motion";
import {
  Edit,
  MoreHorizontal,
  ScrollText,
  Star,
  ToggleLeft,
  ToggleRight,
  Trash2,
} from "lucide-react";
import React from "react";
import { cn } from "../../lib/utils";
import { Icon } from "../Icon";
import { Button } from "../ui/button";
import { Card } from "../ui/card";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "../ui/dropdown-menu";

export interface SkillData {
  id: string;
  organization_id?: string;
  user_id?: string;
  name: string;
  description?: string;
  type?: string;
  status?: string;
  icon?: {
    type: "custom" | "emoji" | "lucide";
    custom?: { url: string };
    emoji?: string;
    lucide?: { name: string; color: string };
  };
  metadata?: any;
  created_at?: string;
  updated_at?: string;
}

export interface SkillCardProps {
  skill: SkillData;
  index?: number;
  onClick?: () => void;
  onAction?: (skill: SkillData, action: string) => void;
  isFavorite?: boolean;
  className?: string;
}

export const SkillCard: React.FC<SkillCardProps> = ({
  skill,
  onClick,
  onAction,
  isFavorite = false,
  className,
}) => {
  const isEnabled = skill.status === "enabled";

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.3 }}
      onClick={onClick}
      className={cn(
        "relative h-full group cursor-pointer",
        !isEnabled && "opacity-60",
        className,
      )}
    >
      <Card className="relative overflow-hidden hover:bg-neutral-50 dark:hover:bg-neutral-200 shadow-sm hover:shadow-md transition-all duration-300 h-full flex flex-col">
        <div className="relative p-6 pb-4">
          <div className="mb-1">
            <div className="flex items-start gap-3 mb-2">
              <div className="flex-shrink-0 mt-0.5">
                {skill.icon && skill.icon.type ? (
                  <Icon icon={skill.icon} size={20} />
                ) : (
                  <ScrollText className="h-5 w-5 text-muted-foreground" />
                )}
              </div>
              <h3 className="font-semibold text-lg text-foreground leading-tight group-hover:text-primary/90 transition-colors duration-300 flex-1">
                {skill.name}
              </h3>
            </div>
          </div>
        </div>

        <div className="relative px-6 pb-6 flex-1 flex flex-col">
          {skill.description && (
            <p className="text-sm text-muted-foreground/80 leading-relaxed line-clamp-2 mb-4">
              {skill.description}
            </p>
          )}

          <div className="flex justify-between items-center mt-auto">
            <div className="flex items-center gap-1.5">
              <div
                className={cn(
                  "relative w-2 h-2 rounded-full",
                  isEnabled ? "bg-lc-active" : "bg-neutral-400",
                )}
              >
                {isEnabled && (
                  <motion.div
                    className="absolute inset-0 bg-lc-active rounded-full"
                    animate={{
                      scale: [1, 1.8, 1],
                      opacity: [0.7, 0, 0.7],
                    }}
                    transition={{
                      duration: 2,
                      repeat: Infinity,
                      ease: "easeInOut",
                    }}
                  />
                )}
              </div>
              <span
                className={cn(
                  "text-xs font-medium",
                  isEnabled
                    ? "text-lc-active dark:text-lc-active"
                    : "text-muted-foreground",
                )}
              >
                {isEnabled ? "Enabled" : "Disabled"}
              </span>
            </div>

            {onAction && (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={(e) => e.stopPropagation()}
                    className="h-7 w-7 p-0 text-muted-foreground ml-2"
                  >
                    <MoreHorizontal className="h-3 w-3" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem
                    onClick={(e) => {
                      e.stopPropagation();
                      onAction(skill, "favorite");
                    }}
                  >
                    <Star
                      className={cn(
                        "h-4 w-4 mr-2",
                        isFavorite ? "text-amber-500 fill-amber-500" : "",
                      )}
                    />
                    {isFavorite ? "Unstar" : "Star"}
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    onClick={(e) => {
                      e.stopPropagation();
                      onAction(skill, "view");
                    }}
                  >
                    <Edit className="h-4 w-4 mr-2" />
                    Edit
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    onClick={(e) => {
                      e.stopPropagation();
                      onAction(skill, isEnabled ? "disable" : "enable");
                    }}
                  >
                    {isEnabled ? (
                      <>
                        <ToggleLeft className="h-4 w-4 mr-2" />
                        Disable
                      </>
                    ) : (
                      <>
                        <ToggleRight className="h-4 w-4 mr-2" />
                        Enable
                      </>
                    )}
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem
                    onSelect={() => {
                      window.setTimeout(() => onAction(skill, "delete"), 0);
                    }}
                    className="text-destructive focus:text-destructive"
                  >
                    <Trash2 className="h-4 w-4 mr-2" />
                    Delete
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            )}
          </div>
        </div>
      </Card>
    </motion.div>
  );
};
